import React, { useEffect, useState } from 'react';
import { Field, Form, Formik } from 'formik';
import {
  Button,
  Grid,
  TextField,
  ListItem,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormLabel,
  FormControlLabel,
  Switch,
  Typography,
  Popover,
  Box,
  IconButton,
} from '@material-ui/core';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import SettingsIcon from '@material-ui/icons/Settings';

import CurrencyTextField from '@unicef/material-ui-currency-textfield/dist/CurrencyTextField';
import { Autocomplete } from '@material-ui/lab';
import UIFormTitle from '../../UI/Form/Title/UIFormTitle';
import UIContainer from '../../UI/Container/UIContainer';

import useStorage from '../../../utils/useStorage';

import { allReferenceTable, isEqualObject } from '../../../utils/utils';
import useApi from '../../../utils/useApi';
import UIDialogConfirm from '../../UI/Dialog/Confirm/UIDialogConfirm';
import UIModalOldForm from '../../UI/ModalOldForm/UIModalOldForm';
import validationSchema from './validationSchema';

const initialState = {
  name: '',
  description: '',
  value: '',
  metadata: {
    referenceTable: 5,
    referenceId: 'self',
    type: false,
    recurrent: {
      activate: false,
      frequency: 'monthly',
      notifyDaysBefore: 0,
      dueDay: 1,
    },
    supplier: null,
  },
};

const frequencies = [
  { id: 'daily', name: 'Diário' },
  { id: 'weekly', name: 'Semanal' },
  { id: 'biweekly', name: 'Quinzenal' },
  { id: 'monthly', name: 'Mensal' },
  { id: 'quarterly', name: 'Trimestral' },
  { id: 'semiannual', name: 'Semestral' },
  { id: 'annual', name: 'Anual' },
];

const CostsNew = (children) => {
  const { collapseNew, changeCollapseNew, suppliersInfo } = children;
  const [open, setOpen] = useState(false);
  const [oldValues, setOldValues] = useStorage('formCosts');
  const [alertList, setAlertList] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorNotify, setAnchorNotify] = useState(null);

  const [registerLoad, registerInfo, setRegisterInfo] = useApi({
    method: 'post',
    url: '/costs',
    onCompleted: () => {
      setAlertList(true);
    },
  });

  function changeModal(isContinue) {
    setOpen(!open);
    if (isContinue) {
      changeCollapseNew();
    }
  }

  function updateStorage(values) {
    setOldValues(values);
  }

  function setFormValues() {
    setOldValues(null);
    setRegisterInfo();
    changeModal();
  }

  useEffect(() => {
    if (oldValues && !isEqualObject(oldValues, initialState)) {
      changeModal();
    }
  }, []);

  function onSubmit(values) {
    const data = { ...values };
    data.metadata.supplier = data.metadata.supplier
      ? data.metadata.supplier.id
      : '1';
    data.metadata.referenceId = data.metadata.referenceId || 'self';
    data.metadata.type = 'SINGLE';

    registerLoad({
      data,
      onCompleted: (res) => {
        if (res) setOldValues(null);
      },
    });
  }

  const handleOpenRecurrent = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseRecurrent = () => {
    setAnchorEl(null);
  };

  const handleOpenNotify = (event) => {
    setAnchorNotify(event.currentTarget);
  };

  const handleCloseNotify = () => {
    setAnchorNotify(null);
  };

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={oldValues || initialState}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ values, errors, setFieldValue, submitForm }) => (
          <Form onBlur={() => updateStorage(values)}>
            <UIContainer container direction="row">
              <ListItem
                button
                onClick={changeCollapseNew}
                style={{ justifyContent: 'space-between' }}
              >
                <UIFormTitle title="Novo Custo" />
                {collapseNew ? <ExpandLess /> : <ExpandMore />}
              </ListItem>
              <Collapse
                in={collapseNew}
                timeout="auto"
                unmountOnExit
                style={{ width: '100%' }}
              >
                <Grid container spacing={1}>
                  <Grid item xs={12} md={4}>
                    <Field
                      name="name"
                      label="Nome do custo"
                      error={Boolean(errors.name)}
                      helperText={errors.name}
                      placeholder="Informe o nome do custo"
                      fullWidth
                      as={TextField}
                      id="tour-costs-new-name"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Field
                      name="description"
                      label="Descrição do custo"
                      placeholder="Informe a descrição do custo"
                      fullWidth
                      as={TextField}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <CurrencyTextField
                      type="tel"
                      value={values.value}
                      label="Valor do custo"
                      error={Boolean(errors.value)}
                      helperText={errors.value}
                      decimalCharacter=","
                      digitGroupSeparator="."
                      currencySymbol="R$"
                      outputFormat="string"
                      fullWidth
                      onChange={(ev, value) => setFieldValue('value', value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      disableClearable
                      noOptionsText="Sem opções"
                      disabled={suppliersInfo.loading}
                      options={
                        suppliersInfo.data !== null ? suppliersInfo.data : []
                      }
                      getOptionLabel={(supplier) => supplier.name}
                      onChange={(ev, supplier) =>
                        setFieldValue('metadata.supplier', supplier)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Fornecedor"
                          name="supplier"
                          variant="standard"
                          placeholder="Informe o fornecedor do custo"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Box display="flex" alignItems="center">
                      <FormControl component="fieldset">
                        <FormLabel component="legend">Recorrência</FormLabel>
                        <FormControlLabel
                          label={
                            values.metadata.recurrent.activate
                              ? 'Ativado'
                              : 'Desativado'
                          }
                          labelPlacement="start"
                          control={
                            <Switch
                              color="primary"
                              id="tour-costs-new-recurrent"
                              checked={values.metadata.recurrent.activate}
                              onChange={(ev) => {
                                setFieldValue(
                                  'metadata.recurrent.activate',
                                  ev.target.checked,
                                );
                                if (ev.target.checked) {
                                  handleOpenRecurrent(ev);
                                }
                              }}
                            />
                          }
                        />
                      </FormControl>
                      {values.metadata.recurrent.activate && (
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={handleOpenRecurrent}
                          id="tour-costs-new-recurrent-settings"
                        >
                          <SettingsIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                    <Popover
                      open={Boolean(anchorEl)}
                      anchorEl={anchorEl}
                      onClose={handleCloseRecurrent}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                      }}
                    >
                      <Box p={3} width={300}>
                        <Typography variant="h6" gutterBottom>
                          Configurar Recorrência
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <FormControl fullWidth>
                              <InputLabel shrink id="recurrent-frequency">
                                Frequência
                              </InputLabel>
                              <Select
                                value={values.metadata.recurrent.frequency}
                                onChange={(ev) =>
                                  setFieldValue(
                                    'metadata.recurrent.frequency',
                                    ev.target.value,
                                  )
                                }
                                labelId="recurrent-frequency"
                                displayEmpty
                              >
                                {frequencies.map(({ id, name }) => (
                                  <MenuItem key={id} value={id}>
                                    {name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          {['monthly', 'quarterly', 'semiannual', 'annual'].includes(
                            values.metadata.recurrent.frequency,
                          ) && (
                              <Grid item xs={12}>
                                <Field
                                  name="metadata.recurrent.dueDay"
                                  label="Vencimento (dia)"
                                  type="number"
                                  fullWidth
                                  as={TextField}
                                />
                              </Grid>
                            )}
                          {['weekly', 'monthly', 'quarterly', 'semiannual', 'annual'].includes(
                            values.metadata.recurrent.frequency,
                          ) && (
                              <Grid item xs={12}>
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="space-between"
                                >
                                  <FormControl component="fieldset">
                                    <FormLabel component="legend">
                                      Notificar antecipadamente
                                    </FormLabel>
                                    <FormControlLabel
                                      label={
                                        values.metadata.recurrent.notifyDaysBefore > 0
                                          ? 'Ativado'
                                          : 'Desativado'
                                      }
                                      labelPlacement="start"
                                      control={
                                        <Switch
                                          color="primary"
                                          checked={values.metadata.recurrent.notifyDaysBefore > 0}
                                          onChange={(ev) => {
                                            const isChecked = ev.target.checked;
                                            setFieldValue(
                                              'metadata.recurrent.notifyDaysBefore',
                                              isChecked ? 1 : 0,
                                            );
                                            if (isChecked) {
                                              handleOpenNotify(ev);
                                            }
                                          }}
                                        />
                                      }
                                    />
                                  </FormControl>
                                  {values.metadata.recurrent.notifyDaysBefore > 0 && (
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={handleOpenNotify}
                                    >
                                      <SettingsIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                </Box>
                                <Popover
                                  open={Boolean(anchorNotify)}
                                  anchorEl={anchorNotify}
                                  onClose={handleCloseNotify}
                                  anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'center',
                                  }}
                                  transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'center',
                                  }}
                                >
                                  <Box p={3} width={250}>
                                    <Typography variant="h6" gutterBottom>
                                      Antecipação
                                    </Typography>
                                    <Grid container spacing={2}>
                                      <Grid item xs={12}>
                                        <Field
                                          name="metadata.recurrent.notifyDaysBefore"
                                          label="Dias de antecedência"
                                          type="number"
                                          fullWidth
                                          as={TextField}
                                          helperText="Quantos dias antes do vencimento gerar a conta?"
                                        />
                                      </Grid>
                                      <Grid item xs={12}>
                                        <Button
                                          fullWidth
                                          variant="contained"
                                          color="primary"
                                          onClick={handleCloseNotify}
                                        >
                                          Confirmar
                                        </Button>
                                      </Grid>
                                    </Grid>
                                  </Box>
                                </Popover>
                              </Grid>
                            )}
                          <Grid item xs={12}>
                            <Button
                              fullWidth
                              variant="contained"
                              color="primary"
                              onClick={handleCloseRecurrent}
                            >
                              Confirmar
                            </Button>
                          </Grid>
                        </Grid>
                      </Box>
                    </Popover>
                  </Grid>
                  <Grid container justify="flex-end" item xs={12}>
                    <Button
                      variant="contained"
                      className="UIFormContainerSave__buttons-canceled"
                      type="reset"
                      onClick={() => setOldValues(initialState)}
                      style={{ marginRight: '25px' }}
                    >
                      Cancelar
                    </Button>
                    <Button
                      onClick={submitForm}
                      className="UIFormContainerSave__buttons-saved"
                      variant="contained"
                      id="tour-costs-new-save"
                    >
                      Adicionar
                    </Button>
                  </Grid>
                </Grid>
              </Collapse>
            </UIContainer>
          </Form>
        )}
      </Formik>
      <UIDialogConfirm
        open={alertList}
        close={() => setAlertList(!alertList)}
        data={registerInfo}
        timeSucess={3000}
        messageSucess="Custo registrado com sucesso"
        setrequestinfo={setRegisterInfo}
      />
      <UIModalOldForm
        open={open}
        changeModal={() => changeModal(true)}
        setFormValues={setFormValues}
      />
    </>
  );
};

export default CostsNew;
